<template>
  <div v-if="filtered">
    <v-data-table
      :headers="headers"
      :items="attendances"
      :server-items-length="attendanceCount"
      :options.sync="tableOptions"
      item-key="id"
      :footer-props="tableFooterOptions"
      :loading="loading"
      loading-text="Đang Tải"
      mobile-breakpoint="0"
      disable-sort
    >
      <!-- Học sinh -->
      <template #item.student="{ item }">
        <CardStudentName :student="item.student" link />
      </template>
      <!-- Ngày sinh -->
      <template #item.dob="{ item }">
        <span>{{ (item.student && item.student.dob) | ddmmyyyy }}</span>
      </template>
      <!-- Ngày -->
      <template #item.date="{ item }">
        {{ item.date | ddmmyyyy }}
      </template>
      <!-- Lớp -->
      <template #item.boardingClassInfo="{ item }">
        <router-link
          style="text-decoration: none"
          :to="'/boarding-class/' + (item.boardingClassInfo && item.boardingClassInfo.id)"
        >
          <span v-if="item.boardingClassInfo">{{ item.boardingClassInfo && item.boardingClassInfo.name }}</span>
        </router-link>
      </template>
      <!-- Trạng thái -->
      <template #item.status="{ item }">
        <v-chip
          small
          style="margin: 0"
          class="white--text"
          v-if="item.status"
          :color="item.status | boardingClassSessionAttendanceStatusColor"
          label
        >
          {{ item.status | boardingClassSessionAttendanceStatusText }}
        </v-chip>
      </template>
      <!-- Thao tác -->
      <template #item.action="{ item }">
        <div v-if="item.status">
            <BoardingClassAttendanceListActions
              :selectedAttendance="item"
              :checkinObject="item.status"
              @attendanceUpdated="searchAttendances(attendanceSearchParams)"
            />
        </div>
        <div v-else>
          <BoardingClassAttendanceListActions
            :selectedAttendance="item"
            :checkinObject="{}"
            @attendanceUpdated="searchAttendances(attendanceSearchParams)"
          />
        </div>
      </template>
    </v-data-table>
  </div>
  <div v-else class="d-flex flex-column align-center justify-center mx-md-4 my-md-4 elevation-0 py-16">
    Vui lòng cung cấp các thông tin ở trên, sau đó ấn [Tìm kiếm] để kiểm tra trạng thái điểm danh
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import moment from 'moment'
import utils from '@/plugins/utils'
import CardStudentName from '@/views/student/components/CardStudentName'
import BoardingClassAttendanceListActions from './BoardingClassAttendanceListActions'

const defaultHeaders = [
  { text: 'Học sinh', value: 'student', width: 200, sortable: true },
  { text: 'Ngày sinh', value: 'dob', width: 100, sortable: false },
  { text: 'Lớp', value: 'boardingClassInfo', width: 100, sortable: false },
  { text: 'Ngày điểm danh', value: 'date', sortable: false, align: 'left', width: 150 },
  { text: 'Trạng thái', value: 'status', sortable: false, align: 'center' },
  { text: 'Thao tác', value: 'action', width: 100, sortable: false },
]

export default {
  components: { BoardingClassAttendanceListActions, CardStudentName },
  data() {
    return {
      filtered: false,
      headers: defaultHeaders,
      tableOptions: {
        page: 1,
        itemsPerPage: 10,
      },
      attendanceSearchParams: {},
      attendanceCount: 0,
      loading: false,
    }
  },
  computed: {
    ...mapState('boardingClassAttendance', ['attendances']),
    tableFooterOptions() {
      return {
        'items-per-page-text': 'Hiển thị mỗi trang',
        'items-per-page-all-text': 'Tất cả',
        'items-per-page': this.tableOptions.itemsPerPage,
        'page-text': utils.paginationText(this.tableOptions, this.attendanceCount),
        itemsPerPageOptions: [10, 20, 40, 100],
      }
    },
  },
  methods: {
    ...mapActions('boardingClassAttendance', ['getManyAttendances', 'getAttendanceCount']),

    async searchAttendances(attendanceFilters) {
      this.filtered = true
      this.loading = true
      if (attendanceFilters) {
        let attendanceParams = {}
        if (attendanceFilters.student) {
          attendanceParams.student = attendanceFilters.student
        } else {
          if (attendanceFilters.class) {
            attendanceParams.class = attendanceFilters.class
          } else if (attendanceFilters.grade) {
            attendanceParams.grade = attendanceFilters.grade
          }
        }
        if (attendanceFilters.status) {
          attendanceParams.status = attendanceFilters.status
        }
        if (attendanceFilters.date) {
          attendanceParams = {
            ...attendanceParams,
            date_gte: moment(attendanceFilters.date).startOf('day').toISOString(),
            date_lte: moment(attendanceFilters.date).endOf('day').toISOString(),
          }
        }

        this.getAttendanceCount(attendanceParams)
          .then(response => {
            this.attendanceCount = response
          })
          .catch(error => {
            console.error(error)
            this.$alert.error('Không lấy được số lượng điểm danh. Lỗi : ', error)
          })

        this.tableOptions.page = 1
        this.attendanceSearchParams = { ...attendanceParams, ...this.pageSetting() }
      } else {
        this.attendanceSearchParams = { ...this.attendanceSearchParams, ...this.pageSetting() }
      }

      try {
        await this.getManyAttendances({ searchParams: this.attendanceSearchParams })
      } catch (error) {
        this.$alert.error('Không lấy được danh sách điểm danh. Lỗi : ', error)
        console.error(error)
      }
      this.loading = false
    },
    pageSetting() {
      return {
        _start: (this.tableOptions.page - 1) * this.tableOptions.itemsPerPage,
        _limit: this.tableOptions.itemsPerPage,
        _sort: 'studentNameToNumber:ASC',
      }
    },
  },
  watch: {
    tableOptions: {
      async handler(newOptions, oldOptions) {
        const itemPerPageChanged = newOptions.itemsPerPage !== oldOptions.itemsPerPage
        const pageChanged = newOptions.page !== oldOptions.page
        if (pageChanged || itemPerPageChanged) {
          await this.getManyAttendances({
            searchParams: {
              ...this.attendanceSearchParams,
              _start: (newOptions.page - 1) * newOptions.itemsPerPage,
              _limit: newOptions.itemsPerPage,
            },
          })
        }
      },
      deep: true,
    },
  },
}
</script>
